import { Cpu, Loader2 } from "lucide-react";
import { useMemo } from "react";
import { useLocale, useTranslations } from "use-intl";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMoney, formatUseCase } from "@/features/usage-logs/components/usage-chart-format";
import { formatUserName } from "@/utils/format-user-name";
import type { UsageMetricsFilter, ResourceUsageLogRow } from "@/features/usage-logs/types";
import { formatRelativeTime } from "./format-relative-time";
import { useResourceUsageQuery } from "./use-resource-usage-query";

interface UsageLogPanelProps {
  sourceEntityType: string;
  sourceEntityId: string;
  filter?: Omit<UsageMetricsFilter, "sourceEntityType" | "sourceEntityId">;
  compact?: boolean;
}

export function UsageLogPanel({ sourceEntityType, sourceEntityId, filter, compact }: UsageLogPanelProps) {
  const t = useTranslations("resource_activity");
  const query = useResourceUsageQuery({ sourceEntityType, sourceEntityId, filter, limit: compact ? 5 : 30 });
  const rows = useMemo(() => query.data?.pages.flatMap((p) => p.items) ?? [], [query.data]);

  if (query.isLoading) return <UsageSkeleton rows={compact ? 3 : 5} />;
  if (query.error) return <p className="py-3 text-sm text-red-500">{t("usage.load_failed")}</p>;
  if (rows.length === 0) return <p className="py-3 text-sm text-[var(--muted-foreground)]">{t("usage.empty")}</p>;

  return (
    <div className={compact ? "space-y-2" : "space-y-3"}>
      <ol className="space-y-2">
        {rows.map((row) => <UsageRow key={row.id} row={row} compact={compact} />)}
      </ol>
      {query.hasNextPage && !compact && (
        <div className="flex justify-center border-t border-[var(--border)] pt-3">
          <Button type="button" variant="ghost" size="sm" onClick={() => query.fetchNextPage()} disabled={query.isFetchingNextPage}>
            {query.isFetchingNextPage ? <Loader2 className="size-4 animate-spin" /> : null}
            {query.isFetchingNextPage ? t("loading_more") : t("load_more")}
          </Button>
        </div>
      )}
    </div>
  );
}

function UsageRow({ row, compact }: { row: ResourceUsageLogRow; compact?: boolean }) {
  const t = useTranslations("resource_activity");
  const usageT = useTranslations("usage_logs");
  const locale = useLocale();
  const actor = formatUserName(row.triggeredByUser, { fallback: t("usage.system_actor") });
  const statusLabel = usageT(`status.${row.status}` as never);
  const tokenText = formatTokenBreakdown(row, compact, {
    cached: usageT("tokens.cached"),
    thinking: usageT("tokens.thinking"),
    cacheWrite: usageT("tokens.cache_write"),
  });
  return (
    <li className="flex min-w-0 items-start gap-3 rounded-md border border-[var(--border)] bg-[var(--background)]/40 px-3 py-2">
      <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-[var(--muted)]">
        <Cpu className="size-3.5 text-[var(--muted-foreground)]" aria-hidden />
      </span>
      <div className="min-w-0 flex-1">
        <div className="flex min-w-0 items-center gap-2">
          <p className="truncate text-sm font-medium text-[var(--foreground)]">{formatUseCase(usageT, row.callType)}</p>
          <span className="shrink-0 rounded-full bg-[var(--accent)] px-2 py-0.5 text-xs text-[var(--accent-foreground)]">
            {statusLabel}
          </span>
        </div>
        <p className={compact ? "mt-0.5 truncate text-xs text-[var(--muted-foreground)]" : "mt-1 truncate text-xs text-[var(--muted-foreground)]"}>
          {actor} · {row.providerName} / {row.modelName}
        </p>
        <p className={compact ? "mt-0.5 text-xs text-[var(--muted-foreground)]" : "mt-1 text-xs text-[var(--muted-foreground)]"}>
          {formatMoney(row.costUsd)} · {tokenText} · {formatRelativeTime(row.createdAt, locale)}
        </p>
      </div>
    </li>
  );
}

function formatTokenBreakdown(row: ResourceUsageLogRow, compact: boolean | undefined, labels: { cached: string; thinking: string; cacheWrite: string }): string {
  const inText = row.tokensInput.toLocaleString();
  const outText = row.tokensOutput.toLocaleString();
  const extras = [
    row.tokensCachedInput > 0 ? `${row.tokensCachedInput.toLocaleString()} ${labels.cached}` : "",
    row.tokensReasoning > 0 ? `${row.tokensReasoning.toLocaleString()} ${labels.thinking}` : "",
    row.tokensCacheWrite > 0 ? `${row.tokensCacheWrite.toLocaleString()} ${labels.cacheWrite}` : "",
  ].filter(Boolean);
  if (compact) {
    return extras.length > 0 ? `${inText} in / ${outText} out / ${extras.join(" / ")}` : `${inText} in / ${outText} out`;
  }
  const total = (row.tokensInput + row.tokensOutput).toLocaleString();
  return extras.length > 0
    ? `${total} tokens (${inText} in / ${outText} out; ${extras.join(" / ")})`
    : `${total} tokens (${inText} in / ${outText} out)`;
}

function UsageSkeleton({ rows }: { rows: number }) {
  return (
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, index) => (
        <div key={index} className="flex items-center gap-3">
          <Skeleton className="size-7 rounded-full" />
          <Skeleton className="h-12 flex-1 rounded" />
        </div>
      ))}
    </div>
  );
}
